"use client";

import { useState } from "react";
import { T } from "@/lib/i18n/T";
import { Heading, Text, ChevronDownIcon } from "@/design-system";
import { HELP_CATEGORIES, type HelpCategory, type FaqEntry } from "./helpData";
import "./help.css";

/* -------------------------------------------------------------------------
 * Help Center — account dashboard › Help.
 *
 * Two levels, no route change (consistent with the dashboard's in-memory
 * section switching): an overview of topic categories (the sub-menu), and a
 * per-category view whose FAQs expand as accordions. State is local so it
 * resets cleanly each time the rail re-enters this section.
 *
 * Design system: typography via <Heading>/<Text> with explicit light tones
 * (the dashboard surface is white), the DS <ChevronDownIcon>, and the ruby
 * <Button> variant for the contact CTA (the only DS variant tuned for a light
 * surface). The accordion / card chrome is bespoke but token-based — there is
 * no DS accordion primitive yet. Every label is wrapped in <L> for Lida.
 * ----------------------------------------------------------------------- */

/** Single FAQ row: a disclosure button + an animated answer panel. */
function FaqItem({
  faq,
  open,
  onToggle,
}: {
  faq: FaqEntry;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `faq-panel-${faq.id}`;
  const btnId = `faq-btn-${faq.id}`;
  return (
    <div className={`rb-help-faq ${open ? "is-open" : ""}`}>
      <button
        id={btnId}
        type="button"
        className="rb-help-faq-q csx3-Rs1 csx3-w-full csx3-g-12"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="rb-help-faq-qtext"><T id={faq.questionId} /></span>
        <ChevronDownIcon className="rb-help-faq-chev" size={20} />
      </button>
      <div id={panelId} role="region" aria-labelledby={btnId} className="rb-help-faq-panel">
        <Text as="p" tone="ghost" size="sm" className="rb-help-faq-a">
          <T id={faq.answerId} />
        </Text>
      </div>
    </div>
  );
}

export function HelpCenter() {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const active: HelpCategory | undefined = activeKey
    ? HELP_CATEGORIES.find((c) => c.key === activeKey)
    : undefined;

  const openCategory = (key: string) => {
    setActiveKey(key);
    setOpenFaq(null);
  };

  /* ---- Overview: the category sub-menu ---- */
  if (!active) {
    return (
      <div className="rb-help csx3-C00 csx3-w-full csx3-g-24">
        <Text tone="ghost" size="sm" className="rb-help-intro">
          <T id="help.intro" />
        </Text>

        <div className="rb-help-cats">
          {HELP_CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className="rb-help-cat csx3-R01 csx3-g-16"
              onClick={() => openCategory(cat.key)}
            >
              <span className="rb-help-cat-ico csx3-R11">{cat.icon}</span>
              <span className="rb-help-cat-body csx3-C00 csx3-g-4 csx3-f1">
                <span className="rb-help-cat-title"><T id={cat.titleId} /></span>
                <span className="rb-help-cat-blurb"><T id={cat.blurbId} /></span>
              </span>
              <ChevronDownIcon className="rb-help-cat-chev" size={18} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* ---- Category view: accordions ---- */
  return (
    <div className="rb-help csx3-C00 csx3-w-full csx3-g-20">
      <button type="button" className="rb-help-back csx3-R01 csx3-g-8" onClick={() => setActiveKey(null)}>
        <ChevronDownIcon className="rb-help-back-chev" size={16} />
        <span><T id="help.allTopics" /></span>
      </button>

      <div className="rb-help-cat-head csx3-R01 csx3-g-12">
        <span className="rb-help-cat-ico csx3-R11">{active.icon}</span>
        <Heading level={3} size="md" tone="onLight" weight="bold">
          <T id={active.titleId} />
        </Heading>
      </div>

      <div className="rb-help-faqs csx3-C00 csx3-w-full">
        {active.faqs.map((faq) => (
          <FaqItem
            key={faq.id}
            faq={faq}
            open={openFaq === faq.id}
            onToggle={() => setOpenFaq((cur) => (cur === faq.id ? null : faq.id))}
          />
        ))}
      </div>
    </div>
  );
}
